import {useMemo} from 'react';
import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {DateRange} from 'rsuite/DateRangePicker';
import {useSearchParams} from 'react-router-dom';

import {getHttpMonitorsFiltersLog} from 'features/http/lib/httpFilters';
import {
    buildLogSearchParams,
    getLogMinDate,
    getLogTimeFromParams,
    validateLogDateRange,
} from 'features/http/lib/httpMonitorLogFiltersView';
import {IHttpMonitorFiltersFormLog} from 'features/http/model/HttpMonitor/IHttpMonitorFiltersFormLog';
import {stringsToDateRange} from 'shared/lib/filterHelper';

export interface IUseHttpMonitorFiltersFormLog {
    form: UseFormReturn<IHttpMonitorFiltersFormLog>,
    filters: ReturnType<typeof getHttpMonitorsFiltersLog>,
    minDate: Date,
    defaultDateRange: DateRange | undefined,
    validateDateRange: (_value: DateRange) => string | undefined,
    changeTime: (_type: string, _time: string) => void,
    submit: ReturnType<UseFormReturn<IHttpMonitorFiltersFormLog>['handleSubmit']>,
}

export const useHttpMonitorFiltersFormLog = (onSuccess: () => void): IUseHttpMonitorFiltersFormLog => {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = getHttpMonitorsFiltersLog(searchParams);
    const urlTime = getLogTimeFromParams(searchParams);
    const defaultDateRange = stringsToDateRange(filters['checkedAt[after]'], filters['checkedAt[before]']);

    const form = useForm<IHttpMonitorFiltersFormLog>({
        mode: 'onSubmit',
        defaultValues: {
            code: filters.code,
            message: filters.message,
            checkedAt: defaultDateRange,
            startTime: urlTime.startTime,
            endTime: urlTime.endTime,
        },
    });

    const minDate = useMemo(() => getLogMinDate(), []);

    const onSubmit: SubmitHandler<IHttpMonitorFiltersFormLog> = async(data) => {
        if (!data.checkedAt || !data.checkedAt[0] || !data.checkedAt[1]) {
            return;
        }

        setSearchParams(buildLogSearchParams(searchParams, {...data, checkedAt: data.checkedAt}));
        onSuccess();
    };

    const changeTime = (type: string, time: string) => {
        form.setValue(type === 'start' ? 'startTime' : 'endTime', time);
    };

    return {
        form,
        filters,
        minDate,
        defaultDateRange,
        validateDateRange: (value) => validateLogDateRange(value, minDate),
        changeTime,
        submit: form.handleSubmit(onSubmit),
    };
};

export default useHttpMonitorFiltersFormLog;
